/**
 * Sale form — multi-item. Each saved item decrements its source lot (sealed
 * or card) and computes the sale's totals (subtotal − fees − shipping − COGS).
 */
import { DB } from "../lib/db";
import { Lists, Settings } from "../lib/prefs";
import { escapeHtml, toast, scrollItemsToBottom, $ } from "../lib/dom";
import { fmt$, fmtN, fmtDate, todayISO } from "../utils/format";
import { openModal, closeModal, markModalDirty } from "../components/modal";
import { formField, readForm } from "../components/formField";
import { wireLotSearch, type LotOption } from "../components/lotSearch";
import { mileageSectionHtml, wireMileageSection, saveMileageSection } from "../components/mileageSection";
import { nextId } from "../utils/ids";
import { render } from "../state";
import { BULK_CLASSES, bulkPoolLotId, bulkUnitValueFor, addToBulkPool, type BulkClass } from "../lib/bulk";
import { holdingPeriod } from "../lib/taxation";
import { reverseEffect, effectForTransaction, type InvEffect } from "../lib/inventoryEffects";
import { cardLockReason } from "../lib/lotLock";
import { endEbayListingsForLots } from "../lib/ebayDelist";

/* In-flight form state. Keyed on `window` so inline onclick handlers can mutate it. */
interface SaleItem {
  "Lot ID"?: string;
  "Item"?: string;
  "Type"?: string;          // "Card" | "Sealed" | "Bulk"
  "Qty": number;
  "Sale Price": number;
  "COGS": number;
  "BulkClass"?: BulkClass;  // present when Type === "Bulk"
  /** Captured from the source lot's Acq Date at sale time so holding period
   *  stays correct even if the source lot is later edited or deleted. */
  "Acq Date"?: string;
  /** Holding days at sale time (sale date - Acq Date). null = Unknown. */
  "Holding Days"?: number | null;
  /** "Short-term" | "Long-term" | "Unknown" - drives tax-rate classification. */
  "Tax Treatment"?: string;
  /** Realized Gain = Sale Price - COGS for this line. */
  "Realized Gain"?: number;
}
interface SaleState {
  "Sale ID": string; "Date": string; "Platform": string; "Status": string;
  "Buyer": string;
  "Fees": number; "Shipping": number; "Notes": string;
  "Items": SaleItem[];
  "_isNew": boolean;
}
function S(): SaleState | null { return (window as any)._saleState as SaleState | null; }

export async function editSale(id?: string): Promise<void> {
  const isNew = !id;
  // Pull lot-bearing tables fresh so the lot search reflects cloud-current
  // qty/FMV. Also refresh sales on edit to hydrate from the latest stored row.
  {
    const refreshes: Promise<void>[] = [DB.refreshTable("sealed"), DB.refreshTable("cards")];
    if (!isNew) refreshes.push(DB.refreshTable("sales"));
    await Promise.all(refreshes);
  }
  const settings = Settings.get();
  const sale = isNew
    ? { "Sale ID": nextId("SAL", "sales", "Sale ID"), "Date": todayISO(), "Platform": "eBay", "Buyer": "",
        "Items": [], "Fees": 0, "Shipping": settings.defaultShippingCost, "Notes": "" }
    : DB.table("sales").find(r => r["Sale ID"] === id);
  if (!sale) { toast("Sale not found", "err"); return; }

  (window as any)._saleState = {
    "Sale ID":        sale["Sale ID"],
    "Date":           sale["Date"],
    "Platform":       sale["Platform"] || "eBay",
    "Buyer":          sale["Buyer"] || "",
    "Status":         String(sale["Status"] || "Completed"),
    "Fees":           Number(sale["Fees"]) || 0,
    "Shipping":       Number(sale["Shipping"]) || 0,
    "Notes":          sale["Notes"] || "",
    "Items":          (sale.Items || []).map((x: any) => ({ ...x })),
    "_isNew":         isNew,
  } as SaleState;

  const body = `
    <form id="saleForm" onsubmit="return false;">
      <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
        ${formField({ label: "Sale ID", name: "Sale ID", value: sale["Sale ID"], required: true })}
        ${formField({ label: "Date", name: "Date", type: "date", value: fmtDate(sale["Date"]), required: true })}
        ${formField({ label: "Platform", name: "Platform", value: sale["Platform"] || "eBay", options: Lists.get("Sale Platform") })}
        ${formField({ label: "Buyer", name: "Buyer", value: sale["Buyer"] || "" })}
        <div class="sm:col-span-2">${formField({ label: "Status", name: "Status", value: String(sale["Status"] || "Completed"), options: [{ value: "Draft", label: "Draft" }, { value: "Completed", label: "Completed" }], required: true, hint: "Drafts don't decrement inventory or hit the books until promoted to Completed." })}</div>
      </div>

      <div class="mb-6">
        <div class="mb-3 pb-2 border-b border-[var(--border)]">
          <h4 class="font-semibold text-emerald-300">Items sold (each decrements its source lot)</h4>
        </div>
        <div class="text-xs text-[var(--muted)] mb-2">Type below to add a sold lot; press Enter without selecting to add a free-text item. COGS auto-fills from the lot's Unit Basis. <b>+ Bulk</b> sells N cards out of one of the three pools (COGS from Configs → Defaults). ${!isNew ? "<span class='text-amber-300'>Editing won\'t re-decrement source lots.</span>" : ""}</div>
        <div id="saleItems" class="space-y-2 max-h-[40vh] overflow-y-auto pr-1"></div>
        <div class="mt-3 flex items-stretch gap-2">
          <div class="combobox-wrapper flex-1 min-w-0" data-sale-add-search>
            <input class="input w-full" autocomplete="off" placeholder="Type to add an item to this sale…">
            <div class="combobox-list"></div>
          </div>
          <button type="button" class="btn btn-ghost !py-1 !px-3 text-xs whitespace-nowrap" onclick="addBulkSaleItem()">+ Bulk</button>
        </div>
      </div>

      <div class="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
        <div>
          ${formField({ label: "Fees ($)", name: "Fees", type: "number", value: sale["Fees"] || 0, hint: `Default eBay ≈ ${settings.defaultEbayFeePct}%` })}
        </div>
        ${formField({ label: "Shipping ($)", name: "Shipping", type: "number", value: sale["Shipping"] || 0 })}
        <div class="flex items-end">
          <button type="button" class="btn btn-ghost w-full" onclick="autoFeeEbay()">Auto-fill eBay fee (subtotal + ship)</button>
        </div>
      </div>

      <div>${formField({ label: "Notes", name: "Notes", type: "textarea", value: sale["Notes"] || "" })}</div>

      ${mileageSectionHtml(sale["Sale ID"], "Post Office")}

      <div class="mt-4 pt-3 pb-3 -mx-4 px-4 border-t border-[var(--border)] grid grid-cols-2 sm:grid-cols-5 gap-3 text-sm sticky bottom-[68px] z-10 bg-[var(--panel)] shadow-[0_-8px_12px_-12px_rgba(0,0,0,0.6)]">
        <div><div class="text-xs text-[var(--muted)]">Subtotal</div><div id="saleSubtotal" class="stat-num font-bold">$0.00</div></div>
        <div><div class="text-xs text-[var(--muted)]">Fees</div><div id="saleFeesDisplay" class="stat-num font-bold">$0.00</div></div>
        <div><div class="text-xs text-[var(--muted)]">Ship</div><div id="saleShipDisplay" class="stat-num font-bold">$0.00</div></div>
        <div><div class="text-xs text-[var(--muted)]">COGS</div><div id="saleCogsDisplay" class="stat-num font-bold">$0.00</div></div>
        <div><div class="text-xs text-[var(--muted)]">Net Profit</div><div id="saleNetDisplay" class="stat-num font-bold">$0.00</div></div>
      </div>
    </form>`;
  const safeId = escapeHtml(sale["Sale ID"]);
  const footer = `
    ${!isNew ? `<button class="btn btn-danger" onclick="deleteRow('sales','Sale ID','${safeId}')">Delete</button>` : ""}
    <button class="btn btn-ghost" onclick="closeModal()">Cancel</button>
    <button class="btn btn-ghost" onclick="saveSaleAsDraft('${isNew ? "" : safeId}')" title="Save as a Draft - inventory is NOT decremented, books are NOT affected. Finish it later from the Sales page.">Save Draft</button>
    <button class="btn btn-primary" onclick="saveSale('${isNew ? "" : safeId}')">${isNew ? "Log" : "Save"}</button>`;
  openModal({ title: isNew ? "Log Sale" : "Edit Sale", body, footer, wide: true });

  // Recompute totals when Fees / Shipping change
  const feesIn = document.querySelector<HTMLInputElement>('#saleForm [name="Fees"]');
  const shipIn = document.querySelector<HTMLInputElement>('#saleForm [name="Shipping"]');
  feesIn?.addEventListener("input", () => { const s = S(); if (s) { s.Fees = Number(feesIn.value) || 0; recomputeSaleTotals(); }});
  shipIn?.addEventListener("input", () => { const s = S(); if (s) { s.Shipping = Number(shipIn.value) || 0; recomputeSaleTotals(); }});
  renderSaleItemsList();
  wireSaleAddSearch();
  wireMileageSection();
}

/* ----- Items add-search at the bottom of the list ----- */
function _addSaleItemFromLot(lot: LotOption): void {
  const s = S(); if (!s) return;
  const itemName = (lot.label.split(" — ")[0] || lot.id).replace(/^[A-Z]+-\d+-\d+ · /, "");
  s.Items.push({
    "Lot ID":     lot.id,
    "Item":       itemName,
    "Type":       lot.type,
    "Qty":        1,
    "Sale Price": +(Number(lot.fmv) || 0).toFixed(2),
    "COGS":       +(Number(lot.basis) || 0).toFixed(2),
  } as any);
  renderSaleItemsList();
  markModalDirty();
  setTimeout(() => {
    const c = document.getElementById("saleItems");
    if (c) c.scrollTop = c.scrollHeight;
  }, 10);
}

function _addSaleItemFreeText(text: string): void {
  const s = S(); if (!s) return;
  s.Items.push({ "Lot ID": "", "Item": text, "Type": "", "Qty": 1, "Sale Price": 0, "COGS": 0 } as any);
  renderSaleItemsList();
  markModalDirty();
}

function wireSaleAddSearch(): void {
  const wrapper = document.querySelector<HTMLElement>("[data-sale-add-search]");
  if (!wrapper) return;
  const inp = wrapper.querySelector<HTMLInputElement>("input");
  if (!inp) return;
  wireLotSearch(wrapper, {
    excludeLocked: true,
    excludeIds: () => {
      const set = new Set<string>();
      const cur = S();
      (cur?.Items || []).forEach((x: any) => { if (x["Lot ID"]) set.add(x["Lot ID"]); });
      return set;
    },
    onSelect: (lot) => {
      _addSaleItemFromLot(lot);
      inp.value = "";
      inp.focus();
    },
  });
  inp.addEventListener("keydown", (e) => {
    if (e.key !== "Enter" || e.defaultPrevented) return;
    const list = wrapper.querySelector<HTMLElement>(".combobox-list");
    const hasActive = list?.querySelector(".combobox-item.active");
    const text = inp.value.trim();
    if (hasActive || !text) return;
    e.preventDefault();
    _addSaleItemFreeText(text);
    inp.value = "";
  });
}

export function addSaleItem(): void {
  const s = S(); if (!s) return;
  s.Items.push({ "Lot ID": "", "Item": "", "Type": "", "Qty": 1, "Sale Price": 0, "COGS": 0 });
  renderSaleItemsList();
  markModalDirty();
  scrollItemsToBottom("saleItems");
}
export function addBulkSaleItem(): void {
  const s = S(); if (!s) return;
  const klass: BulkClass = "Bulk";
  const unit = bulkUnitValueFor(klass);
  s.Items.push({
    "Lot ID":     bulkPoolLotId(klass),
    "Item":       klass,
    "Type":       "Bulk",
    "BulkClass":  klass,
    "Qty":        1,
    "Sale Price": 0,
    "COGS":       +unit.toFixed(2),
  });
  renderSaleItemsList();
  markModalDirty();
  scrollItemsToBottom("saleItems");
}
export function removeSaleItem(idx: number): void {
  const s = S(); if (!s) return;
  s.Items.splice(idx, 1);
  renderSaleItemsList();
  markModalDirty();
}
export function updateSaleItemField(idx: number, field: string, value: string): void {
  const s = S(); if (!s) return;
  const it = s.Items[idx]; if (!it) return;
  (it as any)[field] = (field === "Qty" || field === "Sale Price" || field === "COGS")
    ? (Number(value) || 0) : value;
  // For bulk rows, auto-keep COGS in sync with Qty × pool unit value (unless user already overrode).
  if (it.Type === "Bulk" && it.BulkClass && field === "Qty") {
    const unit = bulkUnitValueFor(it.BulkClass);
    it["COGS"] = +((Number(it.Qty) || 0) * unit).toFixed(2);
  }
  recomputeSaleTotals();
  markModalDirty();
  if (it.Type === "Bulk" && field === "Qty") renderSaleItemsList();
}
/** Switch a bulk-sale row's class — refresh Lot ID, Item label, and COGS to match. */
export function updateSaleBulkClass(idx: number, klass: string): void {
  const s = S(); if (!s) return;
  const it = s.Items[idx]; if (!it) return;
  if (klass !== "Bulk" && klass !== "Holo Bulk" && klass !== "Premium Bulk" && klass !== "$1-$5 Bulk" && klass !== "$5-$10 Bulk") return;
  const k = klass as BulkClass;
  const unit = bulkUnitValueFor(k);
  it["BulkClass"] = k;
  it["Item"]   = k;
  it["Lot ID"] = bulkPoolLotId(k);
  it["COGS"]   = +((Number(it.Qty) || 0) * unit).toFixed(2);
  renderSaleItemsList();
  markModalDirty();
}

function renderSaleItemsList(): void {
  const container = document.getElementById("saleItems");
  if (!container) return;
  const s = S(); if (!s) return;
  const items = s.Items;
  document.getElementById("saleItemsAddMore")?.classList.toggle("hidden", !items.length);
  if (!items.length) {
    container.innerHTML = `<div class="text-sm text-[var(--muted)] italic py-2">No items yet — type below to add a lot, or use + Bulk.</div>`;
    recomputeSaleTotals();
    return;
  }
  container.innerHTML = items.map((item, i) => {
    if (item.Type === "Bulk") {
      const klass = (item.BulkClass || "Bulk") as BulkClass;
      const unit = bulkUnitValueFor(klass);
      const pool = DB.table("cards").find(r => r["Card Lot ID"] === bulkPoolLotId(klass));
      const onHand = Number(pool?.["Qty"]) || 0;
      const pillColor = klass === "Bulk" ? "gray" : (klass === "Holo Bulk" ? "violet" : "amber");
      return `
        <div class="panel p-2 grid grid-cols-12 gap-2 items-end" data-sale-item="${i}">
          <div class="col-span-12 sm:col-span-4 flex items-center gap-2 flex-wrap">
            <span class="pill pill-${pillColor} shrink-0">Bulk sale</span>
            <select class="select !py-1 text-sm w-auto" onchange="updateSaleBulkClass(${i},this.value)">
              ${BULK_CLASSES.map(k => `<option value="${escapeHtml(k)}" ${klass === k ? "selected" : ""}>${escapeHtml(k)}</option>`).join("")}
            </select>
            <span class="text-xs text-[var(--muted)]">On hand: <b>${fmtN(onHand)}</b> @ <b>${fmt$(unit)}</b>/card</span>
          </div>
          <div class="col-span-12 sm:col-span-3 text-xs text-[var(--muted)] self-end pb-1">
            Sells out of the <b>${escapeHtml(klass)}</b> pool. COGS auto-fills from Configs → Defaults.
          </div>
          <div class="col-span-3 sm:col-span-1">
            <label class="label text-xs">Qty</label>
            <input class="input !py-1 text-sm" type="number" step="1" min="1" value="${item.Qty || 1}" oninput="updateSaleItemField(${i},'Qty',this.value)">
          </div>
          <div class="col-span-4 sm:col-span-2">
            <label class="label text-xs">Sale Price ($)</label>
            <input class="input !py-1 text-sm" type="number" step="0.01" value="${item["Sale Price"] || 0}" oninput="updateSaleItemField(${i},'Sale Price',this.value)">
          </div>
          <div class="col-span-4 sm:col-span-1">
            <label class="label text-xs">COGS ($)</label>
            <input class="input !py-1 text-sm" type="number" step="0.01" value="${item["COGS"] || 0}" oninput="updateSaleItemField(${i},'COGS',this.value)">
          </div>
          <div class="col-span-1 flex justify-end items-end">
            <button type="button" class="btn btn-ghost !py-1 !px-2 text-xs" onclick="removeSaleItem(${i})" title="Remove this item">✕</button>
          </div>
        </div>`;
    }
    return `
      <div class="panel p-2 grid grid-cols-12 gap-2 items-end" data-sale-item="${i}">
        <div class="col-span-12 sm:col-span-4">
          <label class="label text-xs">Search inventory</label>
          <div class="combobox-wrapper" data-sale-lot-search="${i}">
            <input class="input !py-1 text-sm" autocomplete="off" placeholder="Type product, card name, or lot ID…"
                   value="${escapeHtml(item["Lot ID"] ? `${item["Lot ID"]} · ${item.Item || ""}`.trim() : (item.Item || ""))}">
            <div class="combobox-list"></div>
          </div>
          <div class="text-xs mt-1">${item["Lot ID"]
            ? `<span class="text-[var(--muted)]">Lot:</span> <code>${escapeHtml(item["Lot ID"])}</code> <span class="pill pill-${item.Type === "Card" ? "violet" : "blue"}">${escapeHtml(item.Type || "")}</span>`
            : `<span class='text-amber-300'>Free-text (no lot link)</span>`}</div>
        </div>
        <div class="col-span-12 sm:col-span-3">
          <label class="label text-xs">Item description</label>
          <input class="input !py-1 text-sm" value="${escapeHtml(item.Item || "")}" oninput="updateSaleItemField(${i},'Item',this.value)">
        </div>
        <div class="col-span-3 sm:col-span-1">
          <label class="label text-xs">Qty</label>
          <input class="input !py-1 text-sm" type="number" step="1" min="1" value="${item.Qty || 1}" oninput="updateSaleItemField(${i},'Qty',this.value)">
        </div>
        <div class="col-span-4 sm:col-span-2">
          <label class="label text-xs">Sale Price ($)</label>
          <input class="input !py-1 text-sm" type="number" step="0.01" value="${item["Sale Price"] || 0}" oninput="updateSaleItemField(${i},'Sale Price',this.value)">
        </div>
        <div class="col-span-4 sm:col-span-1">
          <label class="label text-xs">COGS ($)</label>
          <input class="input !py-1 text-sm" type="number" step="0.01" value="${item["COGS"] || 0}" oninput="updateSaleItemField(${i},'COGS',this.value)">
        </div>
        <div class="col-span-1 flex justify-end items-end">
          <button type="button" class="btn btn-ghost !py-1 !px-2 text-xs" onclick="removeSaleItem(${i})" title="Remove this item">✕</button>
        </div>
      </div>`;
  }).join("");
  // Per-row lot search (skip bulk rows — they use a class dropdown instead)
  items.forEach((item, i) => {
    if (item.Type === "Bulk") return;
    const wrapper = container.querySelector<HTMLElement>(`[data-sale-lot-search="${i}"]`);
    if (!wrapper) return;
    wireLotSearch(wrapper, {
      excludeLocked: true,
      excludeIds: () => {
        const set = new Set<string>();
        const cur = S();
        (cur?.Items || []).forEach((x, j) => { if (j !== i && x["Lot ID"]) set.add(x["Lot ID"]!); });
        return set;
      },
      onSelect: (lot) => {
        const cur = S(); if (!cur) return;
        const it = cur.Items[i];
        it["Lot ID"] = lot.id;
        it["Type"]   = lot.type;
        if (lot.type === "Sealed") {
          const src = DB.table("sealed").find(r => r["Lot ID"] === lot.id);
          if (src) it["Item"] = src["Product"] || "";
        } else {
          const src = DB.table("cards").find(r => r["Card Lot ID"] === lot.id);
          if (src) it["Item"] = `${src["Card Name"] || ""}${src["Set"] ? " · " + src["Set"] : ""}${src["Condition/Grade"] ? " · " + src["Condition/Grade"] : ""}`.trim();
        }
        if (!it["COGS"]) it["COGS"] = +Number(lot.basis).toFixed(2);
        renderSaleItemsList();
        markModalDirty();
      },
    });
  });
  recomputeSaleTotals();
}

function recomputeSaleTotals(): void {
  const s = S(); if (!s) return;
  const items = s.Items || [];
  const subtotal = items.reduce((acc, x) => acc + (Number(x["Sale Price"]) || 0), 0);
  const cogs     = items.reduce((acc, x) => acc + (Number(x["COGS"]) || 0), 0);
  const fees = Number(s.Fees) || 0;
  const ship = Number(s.Shipping) || 0;
  const net  = +(subtotal - fees - ship - cogs).toFixed(2);
  const setText = (id: string, val: string) => { const el = document.getElementById(id); if (el) el.textContent = val; };
  setText("saleSubtotal",    fmt$(subtotal));
  setText("saleFeesDisplay", fmt$(fees));
  setText("saleShipDisplay", fmt$(ship));
  setText("saleCogsDisplay", fmt$(cogs));
  setText("saleNetDisplay",  fmt$(net));
  const netEl = document.getElementById("saleNetDisplay");
  if (netEl) netEl.className = "stat-num font-bold " + (net >= 0 ? "text-emerald-300" : "text-rose-300");
}

export function autoFeeEbay(): void {
  const s = S(); if (!s) return;
  const items = s.Items || [];
  const subtotal = items.reduce((acc, x) => acc + (Number(x["Sale Price"]) || 0), 0);
  const ship = Number(s.Shipping) || 0;
  const pct = Settings.get().defaultEbayFeePct / 100;
  const fee = +((subtotal + ship) * pct).toFixed(2);
  s.Fees = fee;
  const feesIn = document.querySelector<HTMLInputElement>('#saleForm [name="Fees"]');
  if (feesIn) feesIn.value = String(fee);
  recomputeSaleTotals();
  markModalDirty();
}

/** Compare a saved sale's items to the edited items on the inventory-affecting
 *  axes (lot, qty, type, bulk class). Returns true when re-applying inventory
 *  is required. */
function saleInventoryChanged(existing: any, newItems: SaleItem[]): boolean {
  const sig = (arr: any[]) => arr.map(x =>
    `${x.Type || ""}|${x["Lot ID"] || ""}|${x.BulkClass || ""}|${Number(x.Qty) || 0}`
  ).sort().join("~");
  const oldItems = Array.isArray(existing?.Items) ? existing.Items : [];
  return sig(oldItems) !== sig(newItems);
}

export function saveSale(origId: string): void {
  const s = S(); if (!s) return;
  const formData = readForm($("#saleForm"));
  const saleId   = (formData["Sale ID"] || s["Sale ID"] || nextId("SAL", "sales", "Sale ID")).trim();
  const date     = formData["Date"] || todayISO();
  const platform = formData["Platform"] || "";
  const buyer    = formData["Buyer"] || "";
  const status   = String(formData["Status"] || "Completed");
  const isCompleted = status === "Completed";
  const fees     = Number(formData["Fees"]) || 0;
  const shipping = Number(formData["Shipping"]) || 0;
  const notes    = formData["Notes"] || "";

  // Bulk rows have no Item/Lot-ID dependency — keep them when qty > 0.
  const items = (s.Items || []).filter(x =>
    x.Type === "Bulk" ? (Number(x.Qty) || 0) > 0 : (x["Lot ID"] || (x.Item && x.Item.trim()))
  );
  if (!items.length) { toast("Add at least one item.", "err"); return; }

  // A card physically out for grading/repair cannot be sold until its
  // submission is marked Returned. (The database enforces this too.)
  for (const it of items) {
    if (!it["Lot ID"]) continue;
    const lockReason = cardLockReason(it["Lot ID"]!);
    if (lockReason) { toast(`${it["Lot ID"]} is ${lockReason} \u2014 mark it Returned before selling it.`, "err"); return; }
  }

  // Enrich each item with holding-period + realized-gain so Schedule D /
  // Form 8949 mapping is feasible later. Bulk sales use sale date as Acq
  // Date (pool is fungible) -> always short-term. Free-text items (no Lot
  // ID and no auto-fill) end up "Unknown" -> the sales view flags them.
  let unknownHoldingCount = 0;
  for (const it of items) {
    let acq: string | undefined;
    if (it.Type === "Bulk") {
      acq = date;
    } else if (it["Lot ID"]) {
      const sealed = DB.table("sealed").find(r => r["Lot ID"] === it["Lot ID"]);
      const card   = DB.table("cards").find(r => r["Card Lot ID"] === it["Lot ID"]);
      acq = (sealed?.["Acq Date"] || card?.["Acq Date"]) as string | undefined;
    }
    it["Acq Date"] = acq || "";
    const hp = holdingPeriod(acq, date);
    it["Holding Days"]   = hp.days;
    it["Tax Treatment"]  = hp.treatment;
    it["Realized Gain"]  = +(((Number(it["Sale Price"]) || 0) - (Number(it["COGS"]) || 0))).toFixed(2);
    if (hp.treatment === "Unknown") unknownHoldingCount++;
  }

  const subtotal = items.reduce((acc, x) => acc + (Number(x["Sale Price"]) || 0), 0);
  const totalCOGS = items.reduce((acc, x) => acc + (Number(x["COGS"]) || 0), 0);
  const net = +(subtotal - fees - shipping - totalCOGS).toFixed(2);
  const isNew = !origId;
  const existingForStatus = isNew ? null : DB.table("sales").find(r => r["Sale ID"] === origId);
  const wasCompleted = !isNew && (String(existingForStatus?.["Status"] || "Completed") === "Completed");
  if (wasCompleted && !isCompleted) {
    toast("Can't change a completed sale back to draft. Delete it and re-create as a draft instead.", "err");
    return;
  }

  // Decide whether to (re)apply inventory effects. Only Completed sales touch
  // inventory. Drafts never decrement source lots. Promoting a Draft to
  // Completed triggers a fresh apply.
  let applyInventory = false;
  const existing = isNew ? null : DB.table("sales").find(r => r["Sale ID"] === origId);
  if (isCompleted) {
    if (isNew || !wasCompleted) {
      applyInventory = true;
    } else if (saleInventoryChanged(existing, items)) {
      if (!confirm("This sale's items or quantities changed. Reverse the original inventory changes and re-apply with the new values?")) return;
      const oldEffect = effectForTransaction("sales", existing);
      const res = reverseEffect(oldEffect, { excludeId: origId });
      if (!res.ok) { toast("Can't edit: " + res.blockedBy, "err"); return; }
      applyInventory = true;
    }
  }

  let bulkCardsSold = 0;
  const bulkPoolsTouched = new Set<string>();
  let effect: InvEffect = [];

  if (applyInventory) {
    // Decrement source lots for each linked item, recording the exact delta
    // applied so the sale can be reversed precisely on edit/delete.
    for (const it of items) {
      const qty = Number(it.Qty) || 1;
      if (it.Type === "Bulk" && it.BulkClass) {
        addToBulkPool(it.BulkClass, -qty);
        it["Lot ID"] = bulkPoolLotId(it.BulkClass);
        effect.push({ kind: "poolDelta", klass: it.BulkClass, dQty: -qty });
        bulkCardsSold += qty;
        bulkPoolsTouched.add(it.BulkClass);
        continue;
      }
      if (!it["Lot ID"]) continue;
      const sealed = DB.table("sealed").find(r => r["Lot ID"] === it["Lot ID"]);
      const card   = DB.table("cards").find(r  => r["Card Lot ID"] === it["Lot ID"]);
      if (sealed) {
        const curQty = Number(sealed["Qty"]) || 0;
        const oldQty = Math.max(curQty, 1);
        const newQty = Math.max(0, curQty - qty);
        const oldFMV = Number(sealed["Current FMV"]) || 0;
        const newFMV = +(oldFMV / oldQty * newQty).toFixed(2);
        const statusBefore = sealed["Status"];
        const patch: Record<string, any> = {
          "Qty": newQty,
          "Total Basis": +((Number(sealed["Unit Basis"]) || 0) * newQty).toFixed(2),
          "Current FMV": newFMV,
        };
        let statusAfter: string | undefined;
        if (newQty === 0) { patch["Status"] = "Sold"; statusAfter = "Sold"; }
        DB.update("sealed", "Lot ID", it["Lot ID"]!, patch);
        effect.push({ kind: "lotDelta", table: "sealed", lotId: it["Lot ID"]!, dQty: newQty - curQty, dFMV: newFMV - oldFMV, statusBefore, statusAfter });
      } else if (card) {
        const curQty = Number(card["Qty"]) || 0;
        const oldQty = Math.max(curQty, 1);
        const newQty = Math.max(0, curQty - qty);
        const oldFMV = Number(card["Current FMV"]) || 0;
        const newFMV = +(oldFMV / oldQty * newQty).toFixed(2);
        const statusBefore = card["Status"];
        const patch: Record<string, any> = {
          "Qty": newQty,
          "Total Basis": +((Number(card["Unit Basis"]) || 0) * newQty).toFixed(2),
          "Current FMV": newFMV,
        };
        let statusAfter: string | undefined;
        if (newQty === 0) { patch["Status"] = "Sold"; statusAfter = "Sold"; }
        DB.update("cards", "Card Lot ID", it["Lot ID"]!, patch);
        effect.push({ kind: "lotDelta", table: "cards", lotId: it["Lot ID"]!, dQty: newQty - curQty, dFMV: newFMV - oldFMV, statusBefore, statusAfter });
      }
    }
  } else if (isCompleted) {
    // Non-inventory edit on a Completed sale - keep its existing footprint.
    effect = effectForTransaction("sales", existing);
  } else {
    // Draft sales never have an inventory footprint.
    effect = [];
  }

  const saleRow = {
    "Sale ID":        saleId, "Date": date,
    "Platform":       platform, "Buyer": buyer,
    "Status":         status,
    "Items":          items,
    "Subtotal":       +subtotal.toFixed(2),
    "Fees":           +fees.toFixed(2),
    "Shipping":       +shipping.toFixed(2),
    // Drafts don't book COGS or Net Profit - they're zeroed so totals/reports
    // never count them. The Items still carry their own COGS so we can resume.
    "COGS":           isCompleted ? +totalCOGS.toFixed(2) : 0,
    "Net Profit":     isCompleted ? net : 0,
    "Has Unknown Holding": unknownHoldingCount > 0,
    "Inv Effect":     effect,
    "Notes":          notes,
  };
  if (origId) DB.update("sales", "Sale ID", origId, saleRow);
  else        DB.add("sales", saleRow);

  // Optional embedded mileage trip — links to this sale via Related Deal/Lot.
  // (DOM reads happen synchronously, before closeModal tears the form down.)
  void saveMileageSection(saleId, date);

  // eBay sale-sync: a save that came from an imported eBay order marks the
  // order row imported (bridge fn set by lib/ebaySales.ts; no import cycle).
  {
    const m = (window as any)._ebayOrderMark;
    if (m && m.saleId === saleId && isCompleted) {
      (window as any)._ebayOrderMarkFn?.(m.orderId, "imported", saleId);
      delete (window as any)._ebayOrderMark;
    }
  }

  // eBay safety net: end live listings on card lots this sale depleted.
  {
    const depleted = effect
      .filter((e: any) => e.kind === "lotDelta" && (e.table === "cards" || e.table === "sealed") && (Number(e.dQty) || 0) < 0)
      .map((e: any) => String(e.lotId));
    if (depleted.length) void endEbayListingsForLots(depleted, `sold in-app (${saleId})`);
  }

  delete (window as any)._saleState;
  if (!isCompleted) {
    toast(`Draft sale ${saleId} saved (inventory untouched)`);
  } else if (isNew || !wasCompleted) {
    const parts = [`${items.length} item(s) sold`];
    if (bulkCardsSold > 0) parts.push(`${fmtN(bulkCardsSold)} bulk card(s) out of ${bulkPoolsTouched.size} pool(s)`);
    toast(`Sale ${isNew ? "logged" : "completed"} · ${parts.join(" · ")}`);
  } else {
    toast("Sale updated");
  }
  closeModal();
  render();
}

/** Save the current sale form as a Draft - sets Status to Draft and routes
 *  through saveSale so the inventory-skipped path runs. */
export function saveSaleAsDraft(origId: string): void {
  const sel = document.querySelector<HTMLSelectElement>('#saleForm [name="Status"]');
  if (sel) sel.value = "Draft";
  saveSale(origId);
}

// Inline-handler bridges
(window as any).editSale            = editSale;
(window as any).renderSaleItemsList = renderSaleItemsList;
(window as any).saveSale            = saveSale;
(window as any).addSaleItem         = addSaleItem;
(window as any).addBulkSaleItem     = addBulkSaleItem;
(window as any).removeSaleItem      = removeSaleItem;
(window as any).updateSaleItemField = updateSaleItemField;
(window as any).updateSaleBulkClass = updateSaleBulkClass;
(window as any).autoFeeEbay         = autoFeeEbay;
(window as any).saveSaleAsDraft  = saveSaleAsDraft;
